import DocumentIcon from '@heroicons/react/24/outline/DocumentIcon';
import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import PhotoIcon from '@heroicons/react/24/outline/PhotoIcon';
import { useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import LabelValue from '@/components/core/LabelValue/LabelValue';
import { Tooltip } from '@/components/core/Tooltip';
import { LOCAL_STORAGE } from '@/constants';
import { useTranslation } from '@/hooks/useTranslation';
import { AssetApiRecord } from '@/pages/dam/api/assets.types';
import { formatDamCardDate } from '@/pages/dam/utils/damCardDate';
import { useDamAssetContent } from '@/pages/dam/utils/useDamAssetContent';
import { DataModelLayout } from '@/types/DataModel';

import { DamCardBadgeRow } from './DamCardBadgeRow';

interface DamAssetCardProps {
    asset: AssetApiRecord;
    /** The `asset_card` layout; each card area is filled from its section (D-032). */
    layout: DataModelLayout | undefined;
    isSelected?: boolean;
    onSelectedChange?: (isSelected: boolean) => void;
    onEdit?: () => void;
}

/**
 * Asset card of the supplier DAM library (card handoff `7650:282547`). The
 * `asset_card` layout decides which attributes fill the top badge, the badges
 * below the image, the title, the rows and the footer; image, selection and
 * "Edit" are fixed.
 */
function DamAssetCard({ asset, layout, isSelected = false, onSelectedChange, onEdit }: DamAssetCardProps) {
    const { t } = useTranslation();
    const [imageFailed, setImageFailed] = useState(false);
    const { isImage, imageUrl, topBadge, statusBadges, title, rows, footerDate, footerOthers } = useDamAssetContent(asset, layout);
    const showImage = Boolean(imageUrl) && !imageFailed;
    const language = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY) ?? 'en';

    return (
        <article className="tw-flex tw-h-full tw-min-w-0 tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-tertiary tw-bg-fill-primary tw-p-4">
            <div className="tw-relative tw-aspect-square tw-w-full tw-overflow-hidden tw-rounded-md tw-bg-fill-primary">
                {showImage ? (
                    <img
                        src={imageUrl}
                        alt={title}
                        loading="lazy"
                        className="tw-h-full tw-w-full tw-object-contain"
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center" role="img" aria-label={t('DAM.CARD.NO_PREVIEW')}>
                        <Icon icon={isImage ? PhotoIcon : DocumentIcon} size="xl" variant="lighter-gray" />
                    </div>
                )}
                <div className="tw-absolute tw-left-2 tw-right-2 tw-top-2 tw-flex tw-items-start tw-justify-between tw-gap-4">
                    {topBadge ? (
                        <Badge type="category" variant="white" size="xl" icon={topBadge.icon} className="tw-max-w-full tw-overflow-hidden">
                            {topBadge.label}
                        </Badge>
                    ) : <span />}
                    <div className="tw-flex tw-shrink-0 tw-rounded-md tw-bg-fill-primary tw-p-2">
                        <Tooltip popupContent={t('DAM.CARD.ADD_TO_BULK')}>
                            <Checkbox
                                isChecked={isSelected}
                                disabled={!onSelectedChange}
                                aria-label={t('DAM.CARD.ADD_TO_BULK')}
                                onCheckedChange={(checked) => onSelectedChange?.(checked === true)}
                            />
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3">
                <DamCardBadgeRow badges={statusBadges} />
                {title && (
                    <h3 title={title} className="tw-m-0 tw-line-clamp-2 tw-break-words tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                        {title}
                    </h3>
                )}
                <div className="tw-flex tw-flex-col tw-gap-1">
                    {rows.map((row) => (
                        <LabelValue key={row.name} variant="full-width" label={`${row.label}:`} value={row.value} />
                    ))}
                    {footerDate && (
                        <p className="tw-m-0 tw-pt-1 tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                            {formatDamCardDate(t, footerDate, language)}
                        </p>
                    )}
                    {footerOthers.map((field) => (
                        <p key={field.name} className="tw-m-0 tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                            {`${field.label}: ${field.value}`}
                        </p>
                    ))}
                </div>
            </div>

            <Button variant="outline" hasIcon icon={PencilIcon} onClick={onEdit} disabled={!onEdit} className="tw-w-full">
                {t('DAM.CARD.EDIT')}
            </Button>
        </article>
    );
}

export { DamAssetCard };
export type { DamAssetCardProps };
